/**
 * =========================================================
 * CHADHAVA SANKALP
 * =========================================================
 *
 * Handles:
 *
 * 1. Sankalp popup
 * 2. Selected Chadhava synchronization
 * 3. Form validation
 * 4. WhatsApp synchronization
 * 5. Shopify Cart API
 * 6. Line item properties
 * 7. Checkout redirect
 * 8. Error handling
 *
 * Expected selection item:
 *
 * {
 *   id: 123456789,
 *   title: "Special Chadhava",
 *   price: 15100,
 *   quantity: 2
 * }
 *
 * Important:
 *
 * Browser price is used ONLY for display.
 *
 * Shopify determines the actual cart price.
 * =========================================================
 */

(function () {

  'use strict';


  /* =========================================================
     INITIALIZE
  ========================================================= */

  const popup =
    document.querySelector(
      '[data-sankalp-popup]'
    );


  if (!popup) {
    return;
  }


  const modal =
    popup.querySelector(
      '.sankalp-modal'
    );


  const form =
    popup.querySelector(
      '[data-sankalp-form]'
    );


  if (!form) {
    return;
  }


  /* =========================================================
     ELEMENTS
  ========================================================= */

  const nameInput =
    popup.querySelector(
      '#sankalp-devotee-name'
    );


  const gotraInput =
    popup.querySelector(
      '#sankalp-gotra'
    );


  const phoneInput =
    popup.querySelector(
      '#sankalp-phone'
    );


  const whatsappInput =
    popup.querySelector(
      '#sankalp-whatsapp'
    );


  const sameWhatsapp =
    popup.querySelector(
      '[data-same-whatsapp]'
    );


  const selectionItems =
    popup.querySelector(
      '[data-sankalp-selection-items]'
    );


  const selectionCount =
    popup.querySelector(
      '[data-sankalp-selection-count]'
    );


  const totalElement =
    popup.querySelector(
      '[data-sankalp-total]'
    );


  const submitButton =
    popup.querySelector(
      '[data-sankalp-submit]'
    );


  const submitText =
    popup.querySelector(
      '[data-submit-text]'
    );


  const submitLoader =
    popup.querySelector(
      '[data-submit-loader]'
    );


  const formError =
    popup.querySelector(
      '[data-sankalp-form-error]'
    );


  /* =========================================================
     STATE
  ========================================================= */

  let selectedItems = [];

  let lastFocusedElement = null;

  let isSubmitting = false;

  let popupOpened = false;


  /* =========================================================
     FORMAT MONEY
  ========================================================= */

  function formatMoney(cents) {

    const amount =
      Number(cents || 0) / 100;


    try {

      return new Intl.NumberFormat(
        'en-IN',
        {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 2
        }
      ).format(amount);

    } catch (error) {

      return '₹' +
        amount.toFixed(2);

    }

  }


  /* =========================================================
     ESCAPE HTML
  ========================================================= */

  function escapeHTML(value) {

    const div =
      document.createElement(
        'div'
      );


    div.textContent =
      value == null
        ? ''
        : String(value);


    return div.innerHTML;

  }


  /* =========================================================
     NORMALIZE ITEMS
  =========================================================
  
  Prevent malformed variant information from being
  sent to Shopify.
  ========================================================= */

  function normalizeItems(items) {

    if (!Array.isArray(items)) {
      return [];
    }


    const normalized = [];


    items.forEach(function (item) {

      if (!item) {
        return;
      }


      const id =
        Number(item.id);


      const quantity =
        Math.floor(
          Number(item.quantity || 1)
        );


      if (
        !Number.isInteger(id) ||
        id <= 0
      ) {
        return;
      }


      if (
        !Number.isInteger(quantity) ||
        quantity <= 0
      ) {
        return;
      }


      const sellingPlanId =
        item.sellingPlanId
          ? Number(item.sellingPlanId)
          : null;


      const sellingPlanName =
        item.sellingPlanName
          ? String(item.sellingPlanName)
          : null;


      normalized.push({

        id: id,

        title:
          String(
            item.title || 'Chadhava'
          ),

        price:
          Number(item.price || 0),

        quantity: quantity,

        sellingPlanId:
          Number.isInteger(sellingPlanId) &&
          sellingPlanId > 0
            ? sellingPlanId
            : null,

        sellingPlanName:
          sellingPlanName

      });

    });


    return normalized;

  }


  /* =========================================================
     OPEN POPUP
  ========================================================= */

  function openPopup(data) {

    if (!data) {
      return;
    }


    selectedItems =
      normalizeItems(
        data.items
      );


    if (!selectedItems.length) {

      showFormError(
        'Please select at least one Chadhava before continuing.'
      );

      return;

    }


    renderSelection();

    clearErrors();

    setLoading(false);


    lastFocusedElement =
      document.activeElement;


    popup.hidden = false;


    popup.setAttribute(
      'aria-hidden',
      'false'
    );


    document.body.classList.add(
      'sankalp-popup-open'
    );


    popupOpened = true;


    /*
     * Focus modal after it becomes visible.
     */

    setTimeout(
      function () {

        if (modal) {
          modal.focus();
        }

      },
      50
    );

  }


  /* =========================================================
     CLOSE POPUP
  ========================================================= */

  function closePopup() {

    /*
     * Do NOT clear selectedItems.
     *
     * The user may close the popup,
     * change the Chadhava selection,
     * and reopen it.
     */

    popup.hidden = true;


    popup.setAttribute(
      'aria-hidden',
      'true'
    );


    document.body.classList.remove(
      'sankalp-popup-open'
    );


    popupOpened = false;


    /*
     * Stop loading if popup is closed.
     */

    if (isSubmitting) {
      setLoading(false);
    }


    /*
     * Restore focus.
     */

    if (
      lastFocusedElement &&
      typeof lastFocusedElement.focus ===
        'function'
    ) {

      try {

        lastFocusedElement.focus();

      } catch (error) {
        /* Ignore focus errors */
      }

    }

  }


  /* =========================================================
     CLOSE BUTTONS
  ========================================================= */

  popup
    .querySelectorAll(
      '[data-sankalp-close]'
    )
    .forEach(
      function (button) {

        button.addEventListener(
          'click',
          function (event) {

            event.preventDefault();

            closePopup();

          }
        );

      }
    );


  /* =========================================================
     ESC KEY
  ========================================================= */

  document.addEventListener(
    'keydown',
    function (event) {

      if (
        event.key === 'Escape' &&
        !popup.hidden
      ) {

        event.preventDefault();

        closePopup();

      }

    }
  );


  /* =========================================================
     WHATSAPP COPY
  ========================================================= */

  if (
    sameWhatsapp &&
    whatsappInput &&
    phoneInput
  ) {

    sameWhatsapp.addEventListener(
      'change',
      function () {

        if (sameWhatsapp.checked) {

          whatsappInput.value =
            phoneInput.value;

        } else {

          whatsappInput.value =
            '';

        }

      }
    );

  }


  /* =========================================================
     PHONE → WHATSAPP
  ========================================================= */

  if (phoneInput) {

    phoneInput.addEventListener(
      'input',
      function () {

        if (
          sameWhatsapp &&
          sameWhatsapp.checked &&
          whatsappInput
        ) {

          whatsappInput.value =
            phoneInput.value;

        }

      }
    );

  }


  /* =========================================================
     RENDER SELECTION
  ========================================================= */

  function renderSelection() {

    if (!selectionItems) {
      return;
    }


    selectionItems.innerHTML = '';


    let totalQuantity = 0;

    let totalPrice = 0;


    selectedItems.forEach(
      function (item) {

        const quantity =
          Number(item.quantity || 1);


        const price =
          Number(item.price || 0);


        totalQuantity +=
          quantity;


        totalPrice +=
          price * quantity;


        const element =
          document.createElement(
            'div'
          );


        element.className =
          'sankalp-selection-item';


        element.innerHTML = `

          <div>

            <strong>
              ${escapeHTML(item.title)}
            </strong>

            <span>
              ${formatMoney(price)}
              × ${quantity}
            </span>

          </div>

          <strong>
            ${formatMoney(price * quantity)}
          </strong>

        `;


        selectionItems.appendChild(
          element
        );

      }
    );


    if (selectionCount) {

      selectionCount.textContent =
        totalQuantity +
        (
          totalQuantity === 1
            ? ' selected'
            : ' selected'
        );

    }


    if (totalElement) {

      totalElement.textContent =
        formatMoney(totalPrice);

    }

  }


  /* =========================================================
     PHONE VALIDATION
  ========================================================= */

  function validPhone(value) {

    const cleaned =
      String(value || '')
        .replace(/\D/g, '');


    return (
      cleaned.length >= 10 &&
      cleaned.length <= 15
    );

  }


  /* =========================================================
     FORM VALIDATION
  ========================================================= */

  function validateForm() {

    let valid = true;


    clearErrors();


    const name =
      nameInput
        ? nameInput.value.trim()
        : '';


    const gotra =
      gotraInput
        ? gotraInput.value.trim()
        : '';


    const phone =
      phoneInput
        ? phoneInput.value.trim()
        : '';


    const whatsapp =
      whatsappInput
        ? whatsappInput.value.trim()
        : '';


    /* -----------------------------------------
       NAME
    ----------------------------------------- */

    if (!name) {

      showFieldError(
        'devotee_name'
      );

      valid = false;

    }


    /* -----------------------------------------
       GOTRA
    ----------------------------------------- */

    if (!gotra) {

      showFieldError(
        'gotra'
      );

      valid = false;

    }


    /* -----------------------------------------
       PHONE
    ----------------------------------------- */

    if (!validPhone(phone)) {

      showFieldError(
        'phone'
      );

      valid = false;

    }


    /* -----------------------------------------
       WHATSAPP
    ----------------------------------------- */

    if (
      whatsapp &&
      !validPhone(whatsapp)
    ) {

      showFormError(
        'Please enter a valid WhatsApp number.'
      );

      valid = false;

    }


    /* -----------------------------------------
       CHADHAVA
    ----------------------------------------- */

    selectedItems =
      normalizeItems(
        selectedItems
      );


    if (!selectedItems.length) {

      showFormError(
        'Please select at least one Chadhava before continuing.'
      );

      valid = false;

    }


    /* -----------------------------------------
       CONSENT
    ----------------------------------------- */

    const consent =
      form.querySelector(
        '[name="consent"]'
      );


    if (
      consent &&
      !consent.checked
    ) {

      showFormError(
        'Please confirm that the devotee details are correct.'
      );

      valid = false;

    }


    return valid;

  }


  /* =========================================================
     ERROR HELPERS
  ========================================================= */

  function showFieldError(name) {

    const error =
      popup.querySelector(
        '[data-error-for="' +
        name +
        '"]'
      );


    if (error) {
      error.hidden = false;
    }

  }


  function showFormError(message) {

    if (!formError) {
      return;
    }


    formError.textContent =
      message;


    formError.hidden = false;

  }


  function clearErrors() {

    popup
      .querySelectorAll(
        '.sankalp-field-error'
      )
      .forEach(
        function (error) {

          error.hidden = true;

        }
      );


    if (formError) {

      formError.hidden = true;

      formError.textContent = '';

    }

  }


  /* =========================================================
     GET FORM DATA
  ========================================================= */

  function getFormData() {

    const formData =
      new FormData(form);


    return {

      devotee_name:
        String(
          formData.get(
            'devotee_name'
          ) || ''
        ).trim(),


      gotra:
        String(
          formData.get(
            'gotra'
          ) || ''
        ).trim(),


      phone:
        String(
          formData.get(
            'phone'
          ) || ''
        ).trim(),


      whatsapp:
        String(
          formData.get(
            'whatsapp'
          ) || ''
        ).trim(),


      special_message:
        String(
          formData.get(
            'special_message'
          ) || ''
        ).trim()

    };

  }


  /* =========================================================
     BOOKING REFERENCE
  ========================================================= */

  function createBookingReference() {

    const timestamp =
      Date.now()
        .toString(36)
        .toUpperCase();


    const random =
      Math.random()
        .toString(36)
        .substring(2, 7)
        .toUpperCase();


    return (
      'CH-' +
      timestamp +
      '-' +
      random
    );

  }


  /* =========================================================
     BUILD LINE ITEM PROPERTIES
  =========================================================
  
  These properties are attached to EVERY selected
  Chadhava line item.
  
  They will travel with the product into Shopify order data.
  ========================================================= */

  function buildLineItemProperties(
    sankalpData,
    bookingReference
  ) {

    return {

      'Devotee Name':
        sankalpData.devotee_name,


      'Gotra':
        sankalpData.gotra,


      'Phone':
        sankalpData.phone,


      'WhatsApp':
        sankalpData.whatsapp || 'Not provided',


      'Special Sankalp':
        sankalpData.special_message ||
        'No special prayer provided',


      'Chadhava Booking Ref':
        bookingReference

    };

  }


  /* =========================================================
     BUILD CART ITEMS
  ========================================================= */
    function buildCartItems(
      sankalpData,
      bookingReference
      ) {

      const properties =
        buildLineItemProperties(
          sankalpData,
          bookingReference
        );


      return selectedItems.map(
        function (item) {

          const cartItem = {

            id: Number(item.id),

            quantity:
              Number(item.quantity),

            properties: {
              ...properties
            }

          };


          /*
          * Add selling plan only when
          * customer selected a subscription.
          */

          if (
            item.sellingPlanId &&
            Number.isInteger(
              Number(item.sellingPlanId)
            ) &&
            Number(item.sellingPlanId) > 0
          ) {

            cartItem.selling_plan =
              Number(item.sellingPlanId);

          }


          return cartItem;

        }
      );

    }


  /* =========================================================
     ADD TO SHOPIFY CART
  ========================================================= */

  async function addToCart(
    cartItems
  ) {

    if (
      !Array.isArray(cartItems) ||
      !cartItems.length
    ) {

      throw new Error(
        'No Chadhava items were selected.'
      );

    }


    const response =
      await fetch(
        '/cart/add.js',
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            'Accept':
              'application/json'
          },

          body:
            JSON.stringify({
              items: cartItems
            })
        }
      );


    let data = null;


    try {

      data =
        await response.json();

    } catch (error) {

      data = null;

    }


    if (!response.ok) {

      const message =
        data?.description ||
        data?.message ||
        'Unable to add the Chadhava to your cart.';

      throw new Error(
        message
      );

    }


    return data;

  }


  /* =========================================================
     UPDATE CART ATTRIBUTES
  =========================================================
  
  These are an additional backup at cart level.
  
  The important product-specific data is already stored
  as line item properties above.
  ========================================================= */

  async function updateCartAttributes(
    sankalpData,
    bookingReference
  ) {

    try {

      const response =
        await fetch(
          '/cart/update.js',
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            body:
              JSON.stringify({

                attributes: {

                  'Chadhava Booking Ref':
                    bookingReference,

                  'Devotee Name':
                    sankalpData.devotee_name,

                  'Gotra':
                    sankalpData.gotra,

                  'Phone':
                    sankalpData.phone,

                  'WhatsApp':
                    sankalpData.whatsapp ||
                    'Not provided'

                }

              })
          }
        );


      /*
       * Cart attributes are supplementary.
       *
       * If this request fails, don't prevent
       * the customer from checking out because
       * line item properties have already been
       * attached to the products.
       */

      if (!response.ok) {

        console.warn(
          'Chadhava: Cart attributes could not be updated.'
        );

      }

    } catch (error) {

      console.warn(
        'Chadhava: Cart attributes request failed.',
        error
      );

    }

  }


  /* =========================================================
     GET CART
  ========================================================= */

  async function getCart() {

    const response =
      await fetch(
        '/cart.js',
        {
          method: 'GET',

          headers: {
            'Accept':
              'application/json'
          },

          cache: 'no-store'
        }
      );


    if (!response.ok) {

      throw new Error(
        'Unable to verify the cart.'
      );

    }


    return response.json();

  }


  /* =========================================================
     CHECK CART
  ========================================================= */

  async function verifyCart(
    expectedItems
  ) {

    const cart =
      await getCart();


    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {

      throw new Error(
        'Unable to verify your Chadhava cart.'
      );

    }


    /*
     * We only verify that each expected
     * variant exists with at least the
     * requested quantity.
     *
     * For subscription items, also verify
     * the selling plan when Shopify exposes it.
     */

    for (
      const expected of expectedItems
    ) {

      const variantId =
        Number(expected.id);


      const requestedQuantity =
        Number(expected.quantity);


      const matchingItems =
        cart.items.filter(
          function (cartItem) {

            const sameVariant =
              Number(cartItem.variant_id) ===
              variantId;


            if (!sameVariant) {
              return false;
            }


            /*
            * One-time item
            */

            if (!expected.sellingPlanId) {

              return (
              !cartItem.selling_plan_allocation
              );

            }


            /*
            * Subscription item
            */

             return true;

          }
        );


      const cartQuantity =
        matchingItems.reduce(
          function (
            total,
            item
          ) {

            return (
              total +
              Number(item.quantity || 0)
            );

          },
          0
        );


      if (
        cartQuantity <
        requestedQuantity
      ) {

        throw new Error(
          'One of the selected Chadhava items could not be added to your cart.'
        );

      }

    }


    return cart;

  }


  /* =========================================================
     CHECKOUT
  ========================================================= */

  function goToCheckout() {

    /*
     * Use Shopify's checkout URL.
     *
     * Assigning location replaces the current
     * page and lets Shopify handle checkout.
     */

    window.location.assign(
      '/checkout'
    );

  }


  /* =========================================================
     SET LOADING
  ========================================================= */

  function setLoading(
    isLoading
  ) {

    isSubmitting =
      isLoading;


    if (!submitButton) {
      return;
    }


    submitButton.disabled =
      isLoading;


    submitButton.setAttribute(
      'aria-busy',
      isLoading
        ? 'true'
        : 'false'
    );


    if (submitText) {

      submitText.textContent =
        isLoading
          ? 'Preparing Checkout...'
          : 'Continue to Checkout';

    }


    if (submitLoader) {

      submitLoader.hidden =
        !isLoading;

    }

  }


  /* =========================================================
     SUBMIT
  ========================================================= */

  form.addEventListener(
    'submit',
    async function (event) {

      event.preventDefault();


      /*
       * Prevent double submission.
       */

      if (isSubmitting) {
        return;
      }


      clearErrors();


      /*
       * Validate.
       */

      if (!validateForm()) {

        return;

      }


      /*
       * Read current selection AGAIN.
       *
       * This is intentional.
       *
       * It prevents using stale data after
       * the customer changes Chadhava items.
       */

      selectedItems =
        normalizeItems(
          selectedItems
        );


      if (!selectedItems.length) {

        showFormError(
          'Your Chadhava selection is empty. Please select an offering and try again.'
        );

        return;

      }


      const sankalpData =
        getFormData();


      /*
       * Generate a reference for this
       * booking attempt.
       */

      const bookingReference =
        createBookingReference();


      /*
       * Store temporarily in memory only.
       *
       * No localStorage because this contains
       * personal information.
       */

      window.chadhavaSankalp = {

        ...sankalpData,

        booking_reference:
          bookingReference,

        items:
          selectedItems.map(
            function (item) {

              return {
                ...item
              };

            }
          )

      };


      setLoading(true);


      try {

        /* =====================================
           BUILD CART ITEMS
        ===================================== */

        const cartItems =
          buildCartItems(
            sankalpData,
            bookingReference
          );


        /* =====================================
           ADD TO SHOPIFY CART
        ===================================== */

        await addToCart(
          cartItems
        );


        /* =====================================
           VERIFY CART
        ===================================== */

        await verifyCart(
          selectedItems
        );


        /* =====================================
           CART ATTRIBUTES
        ===================================== */

        await updateCartAttributes(
          sankalpData,
          bookingReference
        );


        /*
         * Dispatch success event.
         *
         * Other theme code can listen if needed.
         */

        document.dispatchEvent(
          new CustomEvent(
            'chadhava:cartReady',
            {
              detail: {

                bookingReference:
                  bookingReference,

                items:
                  selectedItems,

                sankalp:
                  sankalpData

              }
            }
          )
        );


        /*
         * Close popup before redirect.
         */

        closePopup();


        /*
         * Go to Shopify checkout.
         */

        goToCheckout();


      } catch (error) {

        console.error(
          'Chadhava checkout error:',
          error
        );


        let message =
          'Something went wrong while preparing your checkout. Please try again.';


        if (
          error &&
          error.message
        ) {

          message =
            error.message;

        }


        showFormError(
          message
        );


        setLoading(false);

      }

    }
  );


  /* =========================================================
     LISTEN FOR CONTINUE TO SANKALP
  ========================================================= */

  document.addEventListener(
    'chadhava:continueToSankalp',
    function (event) {

      if (
        !event.detail ||
        !Array.isArray(
          event.detail.items
        )
      ) {

        return;

      }


      openPopup(
        event.detail
      );

    }
  );


  /* =========================================================
     LIVE SELECTION SYNCHRONIZATION
  =========================================================
  
  If the user changes Chadhava while the
  Sankalp popup is open, update it immediately.
  ========================================================= */

  document.addEventListener(
    'chadhava:selectionChanged',
    function (event) {

      if (
        !event.detail ||
        !Array.isArray(
          event.detail.items
        )
      ) {

        return;

      }


      selectedItems =
        normalizeItems(
          event.detail.items
        );


      /*
       * If popup is currently open,
       * immediately refresh the summary.
       */

      if (popupOpened) {

        renderSelection();

        clearErrors();

      }

    }
  );


  /* =========================================================
     COMPATIBILITY EVENT
  ========================================================= */

  document.addEventListener(
    'chadhava:variantsChanged',
    function (event) {

      if (
        !event.detail ||
        !Array.isArray(
          event.detail.items
        )
      ) {

        return;

      }


      selectedItems =
        normalizeItems(
          event.detail.items
        );


      if (popupOpened) {

        renderSelection();

        clearErrors();

      }

    }
  );


  /* =========================================================
     RESET AFTER SUCCESSFUL ORDER FLOW
  =========================================================
  
  Normally checkout redirects immediately.
  
  This listener is useful if another integration
  completes the flow without redirecting.
  ========================================================= */

  document.addEventListener(
    'chadhava:checkoutCompleted',
    function () {

      try {

        form.reset();

      } catch (error) {
        /* Ignore reset errors */
      }


      selectedItems = [];


      renderSelection();

      clearErrors();

      setLoading(false);

    }
  );


})();